<template>
  <ResponsiveDialog v-model="visible" width="520px" :destroy-on-close="false">
    <template #header>
      <div class="ads__head">
        <button type="button" class="ads__nav" aria-label="이전 날" @click="$emit('prev-day')">
          <el-icon><ArrowLeft /></el-icon>
        </button>
        <div class="ads__title">
          <span class="ads__date">{{ dateLabel }}</span>
          <span v-if="isToday" class="tone-chip tone-chip--primary">오늘</span>
          <span v-if="holiday" class="ads__holiday">{{ holiday }}</span>
        </div>
        <button type="button" class="ads__nav" aria-label="다음 날" @click="$emit('next-day')">
          <el-icon><ArrowRight /></el-icon>
        </button>
      </div>
    </template>

    <div ref="bodyRef" class="ads__body">
      <div class="ads__summary">
        일정 <b class="u-num">{{ schedules.length }}</b> · 업무 <b class="u-num">{{ tasks.length }}</b>
      </div>

      <div v-if="loading" class="ads__skel">
        <div v-for="i in 3" :key="i" class="ads__skel-row skel-row" />
      </div>

      <div v-else-if="!items.length" class="empty-state">
        <div class="empty-state__icon">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round">
            <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" /><path d="M3.5 10h17M8 3v4M16 3v4" />
          </svg>
        </div>
        <div class="empty-state__title">이 날은 비어 있습니다</div>
        <div class="empty-state__hint">아래에서 일정이나 업무를 추가할 수 있습니다</div>
      </div>

      <template v-else>
        <!-- 일정 -->
        <button
          v-for="it in schedules"
          :key="it.uid"
          type="button"
          class="ads__row"
          @click="$emit('open-item', it)"
        >
          <span class="ads__gutter u-num">{{ startLabel(it) }}</span>
          <span class="ads__bar" :style="{ background: it.color }" />
          <span class="ads__main">
            <span class="ads__name">{{ it.title }}</span>
            <span class="ads__sub">
              <span class="ads__type" :style="{ color: it.color }">{{ it.typeLabel }}</span>
              <span v-if="!it.allDay" class="u-num">{{ timeRangeLabel(it) }}</span>
              <span v-if="it.location" class="ads__loc">
                <el-icon><LocationFilled /></el-icon>{{ it.location.name }}
              </span>
              <span v-if="it.owner" class="ads__who">
                <el-avatar :size="18" :src="avatarOf(it.owner)">{{ initialOf(it.owner) }}</el-avatar>
                {{ it.owner.nickname }}
              </span>
            </span>
          </span>
        </button>

        <!-- 업무 -->
        <div v-for="it in tasks" :key="it.uid" class="ads__row ads__row--task">
          <button
            type="button"
            class="ads__check"
            :aria-label="it.status === 'done' ? '완료 취소' : '완료 처리'"
            :disabled="!it.canProgress"
            @click.stop="$emit('toggle-done', it)"
          >
            <span class="ads__check-box" :class="{ 'is-done': it.status === 'done' }">
              <el-icon v-if="it.status === 'done'"><Select /></el-icon>
            </span>
          </button>

          <button type="button" class="ads__main ads__main--btn" @click="$emit('open-item', it)">
            <span class="ads__name" :class="{ 'is-done': it.status === 'done' }">{{ it.title }}</span>
            <span class="ads__sub">
              <StatusPill :status="it.status" />
              <span v-if="it.priority === 'high'" class="ads__pri">매우중요</span>
              <span v-if="it.owner" class="ads__who">
                <el-avatar :size="18" :src="avatarOf(it.owner)">{{ initialOf(it.owner) }}</el-avatar>
                {{ it.owner.nickname }}
              </span>
              <span v-else class="ads__unassigned">미배정</span>
              <span v-if="it.dueDate" class="ads__due u-num" :class="{ 'is-overdue': it.isOverdue }">
                {{ formatDueDate(it) }}
              </span>
            </span>
          </button>
        </div>
      </template>
    </div>

    <template #footer>
      <el-button class="ads__add" @click="$emit('compose', { kind: 'schedule', date })">＋ 일정</el-button>
      <el-button class="ads__add" type="primary" @click="$emit('compose', { kind: 'task', date })">＋ 업무</el-button>
    </template>
  </ResponsiveDialog>
</template>

<script>
import { computed, ref } from 'vue'
import { ArrowLeft, ArrowRight, LocationFilled, Select } from '@element-plus/icons-vue'
import ResponsiveDialog from '@/components/dialogs/ResponsiveDialog.vue'
import StatusPill from '@/components/tasks/StatusPill.vue'
import { useSwipeNavigation } from '@/composables/useSwipeNavigation'
import { useHolidays } from '@/composables/useHolidays'
import { timeRangeLabel, timeLabel, ymd } from '@/utils/agendaItem'
import { getProfileImageUrl, getProfileInitial } from '@/utils/profileUtils'
import { formatDueDate as fmtDue } from '@/utils/taskUtils'

/**
 * 일정 + 업무를 한 목록으로 보여주는 일자 시트.
 * DayAgendaSheet(511줄, 일정 전용) + TaskDaySheet(109줄, 업무 전용) 통합.
 *
 * 두 가지를 바로잡는다:
 *  1. v-model 을 쓴다. DayAgendaSheet 만 visible+close 조합이라 이 코드베이스에서
 *     혼자 달랐다.
 *  2. 스와이프를 useSwipeNavigation 으로 바꾼다. DayAgendaSheet 는 세로 가드가 없는
 *     자체 구현이라 긴 목록을 세로로 스크롤하다 날짜가 바뀌는 실버그가 있었다.
 */
export default {
  name: 'AgendaDaySheet',
  components: { ResponsiveDialog, StatusPill, ArrowLeft, ArrowRight, LocationFilled, Select },
  props: {
    modelValue: { type: Boolean, default: false },
    /** 'YYYY-MM-DD' */
    date: { type: String, required: true },
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false }
  },
  emits: ['update:modelValue', 'prev-day', 'next-day', 'open-item', 'toggle-done', 'compose'],
  setup(props, { emit }) {
    const bodyRef = ref(null)

    const visible = computed({
      get: () => props.modelValue,
      set: (v) => emit('update:modelValue', v)
    })

    useSwipeNavigation(bodyRef, {
      onPrev: () => emit('prev-day'),
      onNext: () => emit('next-day')
    })

    const year = computed(() => Number((props.date || '').slice(0, 4)) || new Date().getFullYear())
    const { holidayName } = useHolidays(year)
    const holiday = computed(() => holidayName(props.date))

    const isToday = computed(() => props.date === ymd(new Date()))

    /**
     * 좌측 거터에 들어가는 짧은 시각. 목록의 모든 제목이 같은 x 에서 시작하게
     * 만드는 게 목적이다. 예전에는 시각이 제목 위 메타 줄 안에 있어서
     * 항목마다 제목 시작 위치가 달랐고, 세로로 훑을 수가 없었다.
     */
    function startLabel(item) {
      if (item.allDay) return '종일'
      return timeLabel(item) || '—'
    }

    const dateLabel = computed(() => {
      if (!props.date) return ''
      const d = new Date(`${props.date}T00:00:00`)
      const w = ['일', '월', '화', '수', '목', '금', '토'][d.getDay()]
      return `${d.getMonth() + 1}월 ${d.getDate()}일 (${w})`
    })

    // 일정 먼저, 그다음 업무 — 시간이 있는 것이 위로
    const schedules = computed(() =>
      props.items.filter((i) => i.kind === 'schedule')
        .sort((a, b) => (a.allDay === b.allDay ? (a.startAt || '').localeCompare(b.startAt || '') : a.allDay ? 1 : -1))
    )
    const tasks = computed(() =>
      props.items.filter((i) => i.kind === 'task')
        .sort((a, b) => {
          const rank = (s) => (s === 'done' ? 2 : s === 'in_progress' ? 0 : 1)
          return rank(a.status) - rank(b.status) || (a.title || '').localeCompare(b.title || '')
        })
    )

    function formatDueDate(item) {
      return fmtDue(item.dueDate)
    }

    const avatarOf = (u) => getProfileImageUrl(u)
    const initialOf = (u) => getProfileInitial(u)

    return {
      bodyRef, visible, dateLabel, holiday, isToday, schedules, tasks,
      timeRangeLabel, startLabel, formatDueDate, avatarOf, initialOf
    }
  }
}
</script>

<style scoped>
.ads__head {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  flex: 1;
  min-width: 0;
}

.ads__nav {
  width: var(--tap-min);
  height: var(--tap-min);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: none;
  border-radius: var(--r-full);
  color: var(--color-text-muted);
  cursor: pointer;
  flex-shrink: 0;
}
.ads__nav:active { background: var(--color-surface-2); }

.ads__title { flex: 1; min-width: 0; display: flex; align-items: baseline; gap: var(--sp-2); }
.ads__date { font-size: var(--fs-lg); font-weight: var(--fw-semibold); color: var(--color-text); }
.ads__holiday { font-size: var(--fs-xs); color: var(--color-danger); }

.ads__body { min-height: 120px; }

.ads__summary {
  font-size: var(--fs-xs);
  color: var(--color-text-muted);
  padding-bottom: var(--sp-2);
  border-bottom: 1px solid var(--color-border);
  margin-bottom: var(--sp-2);
}

.ads__row {
  width: 100%;
  display: flex;
  align-items: stretch;
  gap: var(--sp-2);
  padding: var(--sp-2) 0;
  border: 0;
  border-bottom: 1px solid var(--color-border);
  background: none;
  text-align: left;
  cursor: pointer;
  min-height: var(--tap-comfortable);
}
.ads__row:last-child { border-bottom: 0; }
.ads__row:active { background: var(--color-surface-2); }
.ads__row--task { cursor: default; }

/*
 * 시각 거터. 폭을 고정해야 제목들이 한 줄로 선다.
 * 업무 행의 체크박스도 같은 폭(--tap-min 44px)을 차지하므로
 * 일정과 업무가 섞여 있어도 제목 시작점이 흔들리지 않는다.
 */
.ads__gutter {
  width: var(--tap-min);
  flex: 0 0 var(--tap-min);
  padding-top: 2px;
  font-size: var(--fs-xs);
  color: var(--color-text-muted);
  text-align: right;
  padding-right: var(--sp-2);
}

.ads__bar { width: 3px; border-radius: var(--r-full); flex-shrink: 0; }

.ads__type { font-weight: var(--fw-semibold); }

.ads__check {
  width: var(--tap-min);
  flex: 0 0 var(--tap-min);
  height: var(--tap-min);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: none;
  cursor: pointer;
  flex-shrink: 0;
  align-self: center;
}
.ads__check:disabled { opacity: 0.35; cursor: not-allowed; }

.ads__check-box {
  width: 22px;
  height: 22px;
  border: 2px solid var(--color-border-strong);
  border-radius: var(--r-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-on-accent);
  font-size: var(--fs-md);
}
.ads__check-box.is-done { background: var(--color-success); border-color: var(--color-success); }

.ads__main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.ads__main--btn { border: 0; background: none; text-align: left; cursor: pointer; padding: 0; }

.ads__who { display: inline-flex; align-items: center; gap: 4px; }
.ads__who :deep(.el-avatar) { border-width: 1px; box-shadow: none; }

.ads__pri { font-size: var(--fs-2xs); color: var(--color-danger); font-weight: var(--fw-semibold); }

.ads__name {
  font-size: var(--fs-md);
  font-weight: var(--fw-medium);
  color: var(--color-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ads__name.is-done { color: var(--color-text-subtle); text-decoration: line-through; }

.ads__sub {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  flex-wrap: wrap;
  font-size: var(--fs-xs);
  color: var(--color-text-muted);
}

.ads__loc { display: inline-flex; align-items: center; gap: 2px; }
.ads__unassigned { color: var(--color-warning); }
.ads__due.is-overdue { color: var(--color-danger); font-weight: var(--fw-semibold); }

/* 스피너 대신 형태를 먼저 보여준다 — 시트가 더 빨리 열린 것처럼 느껴진다.
   shimmer 는 styles/primitives.css 의 .skel-row 로 옮겼다(세 화면이 각자
   복제하고 있었고 애니메이션 길이가 서로 달랐다). */
.ads__skel { display: flex; flex-direction: column; gap: var(--sp-3); padding: var(--sp-2) 0; }
.ads__skel-row { height: 44px; }

/* 시트 하단의 주 액션. 한 손 엄지로 누르는 자리라 44 로는 얇다. */
.ads__add { min-height: var(--tap-lg); font-weight: var(--fw-semibold); }
</style>
